import { useMemo } from 'react';
import { Link, useMatches } from '@tanstack/react-router';
import type { Breadcrumb } from '@interxfi/design-system/components/breadcrumbs/breadcrumbs';

export function buildBreadcrumb(breadcrumb: Omit<Breadcrumb, 'LinkElement'>): Breadcrumb {
  return {
    LinkElement: Link,
    ...breadcrumb,
  } as const;
}

export interface AggregateBreadcrumb {
  breadcrumb: Breadcrumb;
}

export interface FullBreadcrumbs {
  breadcrumbs: Breadcrumb[];
}

export function buildBreadcrumbs(breadcrumbs: Omit<Breadcrumb, 'LinkElement'>[]): FullBreadcrumbs {
  return {
    breadcrumbs: breadcrumbs.map(buildBreadcrumb),
  } as const;
}

export function useBreadcrumbs(showMax: number = 2) {
  const matches = useMatches();

  return useMemo(() => {
    const breadcrumbs: Breadcrumb[] = [];
    const lastMatchIndex = matches.length - 1;
    for (let i = lastMatchIndex; i >= 0; i -= 1) {
      const match = matches[i];

      if (i === lastMatchIndex && match.loaderData && 'breadcrumbs' in match.loaderData) {
        return match.loaderData.breadcrumbs;
      }

      if (match.loaderData && 'breadcrumb' in match.loaderData) {
        breadcrumbs.unshift(match.loaderData.breadcrumb);
      }
    }

    // If the last match has a breadcrumb, we only want to show the last x matches
    if (showMax) {
      return breadcrumbs.slice(-showMax);
    }

    return breadcrumbs;
  }, [matches, showMax]) as Breadcrumb[];
}
